import { useState, useMemo, useEffect, useCallback } from 'react';
import { useRestaurant } from '@/lib/session';
import {
  fetchCustomers,
  upsertCustomer,
  deleteCustomer,
  redeemLoyaltyPoints,
  adjustLoyaltyPoints,
  adjustWallet,
  getLoyaltyConfig,
  tierForPoints,
  type Customer,
} from '@/lib/data';

const segments = ['All', 'VIP', 'Regular', 'New', 'Lapsed'] as const;

type CustFormData = {
  name: string;
  phone: string;
  email: string;
  segment: Customer['segment'];
  dietaryPrefs: string;
  tags: string;
};

const emptyCustForm: CustFormData = {
  name: '', phone: '', email: '', segment: 'New', dietaryPrefs: '', tags: '',
};

export default function CustomersPage() {
  const restaurant = useRestaurant();
  const loyaltyCfg = getLoyaltyConfig(restaurant);
  const [search, setSearch] = useState('');
  const [segmentFilter, setSegmentFilter] = useState<string>('All');
  const [sortKey, setSortKey] = useState<'totalSpend' | 'totalVisits' | 'lastVisit'>('totalSpend');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);

  const [custs, setCusts] = useState<Customer[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [saving, setSaving] = useState(false);

  // Add / Edit Customer modal
  const [custModalOpen, setCustModalOpen] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState<Customer | null>(null);
  const [custForm, setCustForm] = useState<CustFormData>({ ...emptyCustForm });
  const [loyaltyToast, setLoyaltyToast] = useState<string | null>(null);
  const [actionError, setActionError] = useState('');

  // Delete confirm
  const [deleteTarget, setDeleteTarget] = useState<Customer | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError('');
    try {
      setCusts(await fetchCustomers(restaurant.id));
    } catch (e) {
      setLoadError(e instanceof Error ? e.message : 'Could not load customers.');
    } finally {
      setLoading(false);
    }
  }, [restaurant.id]);

  useEffect(() => { load(); }, [load]);

  const showActionError = (e: unknown) => {
    setActionError(e instanceof Error ? e.message : 'Something went wrong.');
    setTimeout(() => setActionError(''), 4000);
  };

  const filteredCustomers = useMemo(() => {
    let result = [...custs];

    if (segmentFilter !== 'All') {
      result = result.filter((c) => c.segment === segmentFilter);
    }
    if (search.trim()) {
      const q = search.toLowerCase();
      result = result.filter(
        (c) =>
          c.name.toLowerCase().includes(q) ||
          (c.phone ?? '').includes(q) ||
          (c.email ?? '').toLowerCase().includes(q)
      );
    }

    result.sort((a, b) => {
      let cmp = 0;
      if (sortKey === 'totalSpend') cmp = a.totalSpend - b.totalSpend;
      else if (sortKey === 'totalVisits') cmp = a.totalVisits - b.totalVisits;
      else cmp = (a.lastVisit ?? '').localeCompare(b.lastVisit ?? '');
      return sortDir === 'desc' ? -cmp : cmp;
    });

    return result;
  }, [custs, search, segmentFilter, sortKey, sortDir]);

  const toggleSort = (key: typeof sortKey) => {
    if (sortKey === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir('desc');
    }
  };

  // KPIs computed from live data
  const now = new Date();
  const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
  const monthName = now.toLocaleDateString('en-IN', { month: 'long' });
  const activeThisMonth = custs.filter((c) => c.lastVisit && new Date(c.lastVisit) >= monthStart).length;
  const newThisMonth = custs.filter((c) => c.joinedDate && new Date(c.joinedDate) >= monthStart).length;
  const avgLifetimeValue = custs.length > 0
    ? Math.round(custs.reduce((s, c) => s + c.totalSpend, 0) / custs.length)
    : 0;

  const openAddCustModal = () => {
    setEditingCustomer(null);
    setCustForm({ ...emptyCustForm });
    setCustModalOpen(true);
  };

  const openEditCustModal = (customer: Customer) => {
    setEditingCustomer(customer);
    setCustForm({
      name: customer.name,
      phone: customer.phone ?? '',
      email: customer.email ?? '',
      segment: customer.segment,
      dietaryPrefs: customer.dietaryPrefs.join(', '),
      tags: customer.tags.join(', '),
    });
    setCustModalOpen(true);
  };

  const handleCustSave = async () => {
    if (!custForm.name.trim() || !custForm.phone.trim() || saving) return;
    setSaving(true);
    try {
      const base = {
        name: custForm.name.trim(),
        phone: custForm.phone.trim() || null,
        email: custForm.email.trim() || null,
        segment: custForm.segment,
        dietaryPrefs: custForm.dietaryPrefs ? custForm.dietaryPrefs.split(',').map((s) => s.trim()).filter(Boolean) : [],
        tags: custForm.tags ? custForm.tags.split(',').map((s) => s.trim()).filter(Boolean) : [],
      };
      if (editingCustomer) {
        const saved = await upsertCustomer(restaurant.id, { ...base, id: editingCustomer.id });
        setCusts((prev) => prev.map((c) => (c.id === saved.id ? saved : c)));
        setSelectedCustomer((prev) => (prev && prev.id === saved.id ? saved : prev));
      } else {
        const saved = await upsertCustomer(restaurant.id, {
          ...base,
          totalVisits: 0,
          totalSpend: 0,
          avgOrderValue: 0,
          loyaltyPoints: 0,
          lastVisit: null,
          joinedDate: new Date().toISOString(),
        });
        setCusts((prev) => [saved, ...prev]);
      }
      setCustModalOpen(false);
      setEditingCustomer(null);
    } catch (e) {
      showActionError(e);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (customer: Customer) => {
    try {
      await deleteCustomer(restaurant.id, customer.id);
      setCusts((prev) => prev.filter((c) => c.id !== customer.id));
      setDeleteTarget(null);
      setSelectedCustomer(null);
    } catch (e) {
      showActionError(e);
    }
  };

  const applyNewBalance = (customer: Customer, newBalance: number, toast: string) => {
    const updated = { ...customer, loyaltyPoints: newBalance };
    setCusts((prev) => prev.map((c) => (c.id === customer.id ? updated : c)));
    setSelectedCustomer((prev) => (prev && prev.id === customer.id ? updated : prev));
    setLoyaltyToast(toast);
    setTimeout(() => setLoyaltyToast(null), 2500);
  };

  const handleRedeemPoints = async (customer: Customer) => {
    const input = window.prompt(
      `Redeem points for ${customer.name}\nBalance: ${customer.loyaltyPoints} points\nHow many points to redeem?`,
    );
    if (!input) return;
    const points = Math.floor(Number(input));
    if (!Number.isFinite(points) || points <= 0) { showActionError(new Error('Enter a valid number of points.')); return; }
    try {
      const newBalance = await redeemLoyaltyPoints(restaurant.id, customer, points);
      applyNewBalance(customer, newBalance, `Redeemed ${points} points for ${customer.name}`);
    } catch (e) {
      showActionError(e);
    }
  };

  const handleAdjustPoints = async (customer: Customer) => {
    const input = window.prompt(
      `Adjust points for ${customer.name} (use a negative number to deduct)\nBalance: ${customer.loyaltyPoints} points`,
    );
    if (!input) return;
    const delta = Math.floor(Number(input));
    if (!Number.isFinite(delta) || delta === 0) { showActionError(new Error('Enter a non-zero number.')); return; }
    try {
      const newBalance = await adjustLoyaltyPoints(restaurant.id, customer, delta, 'Manual adjustment');
      applyNewBalance(customer, newBalance, `${delta > 0 ? '+' : ''}${delta} points for ${customer.name}`);
    } catch (e) {
      showActionError(e);
    }
  };

  const handleWallet = async (customer: Customer, sign: 1 | -1) => {
    const verb = sign > 0 ? 'Add credit to' : 'Deduct from';
    const input = window.prompt(`${verb} ${customer.name}'s wallet (₹)\nBalance: ₹${customer.walletBalance ?? 0}`);
    if (!input) return;
    const amt = Math.abs(Number(input));
    if (!Number.isFinite(amt) || amt <= 0) { showActionError(new Error('Enter a valid amount.')); return; }
    try {
      const newBalance = await adjustWallet(restaurant.id, customer, sign * amt, sign > 0 ? 'topup' : 'spend');
      const updated = { ...customer, walletBalance: newBalance };
      setCusts((prev) => prev.map((c) => (c.id === customer.id ? updated : c)));
      setSelectedCustomer((prev) => (prev && prev.id === customer.id ? updated : prev));
      setLoyaltyToast(`Wallet ${sign > 0 ? 'credited' : 'debited'} ₹${amt} for ${customer.name}`);
      setTimeout(() => setLoyaltyToast(null), 2500);
    } catch (e) {
      showActionError(e);
    }
  };

  return (
    <div className="p-6 space-y-5">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-lg font-bold text-foreground-800">Customers</h2>
          <p className="text-sm text-foreground-500 mt-0.5">
            {custs.length} total · {activeThisMonth} active this month · {newThisMonth} new
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={openAddCustModal}
            className="px-4 py-2 text-sm font-medium rounded-lg bg-accent-500 text-background-50 hover:bg-accent-600 transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-add-line mr-1"></i> Add Customer
          </button>
          <button className="px-4 py-2 text-sm font-medium rounded-lg border border-background-200 bg-background-50 text-foreground-700 hover:border-background-300 transition-colors cursor-pointer whitespace-nowrap">
            <i className="ri-mail-send-line mr-1"></i> Campaign
          </button>
        </div>
      </div>

      {/* Loyalty Toast */}
      {loyaltyToast && (
        <div className="fixed top-5 right-5 z-50 px-4 py-3 rounded-lg bg-green-50 border border-green-200 text-green-700 text-sm flex items-center gap-2 shadow-sm animate-[slideInRight_0.25s_ease-out]">
          <i className="ri-checkbox-circle-line text-green-500"></i>
          {loyaltyToast}
        </div>
      )}

      {/* Error Toast */}
      {actionError && (
        <div className="fixed top-5 right-5 z-50 px-4 py-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm flex items-center gap-2 shadow-sm animate-[slideInRight_0.25s_ease-out]">
          <i className="ri-error-warning-line text-red-500"></i>
          {actionError}
        </div>
      )}

      {/* KPI Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <div className="bg-background-50 border border-background-200 rounded-lg p-4">
          <p className="text-xs text-foreground-500 mb-1">Total Customers</p>
          <p className="text-xl font-bold text-foreground-800">{custs.length}</p>
        </div>
        <div className="bg-background-50 border border-background-200 rounded-lg p-4">
          <p className="text-xs text-foreground-500 mb-1">Active This Month</p>
          <p className="text-xl font-bold text-green-600">{activeThisMonth}</p>
        </div>
        <div className="bg-background-50 border border-background-200 rounded-lg p-4">
          <p className="text-xs text-foreground-500 mb-1">New ({monthName})</p>
          <p className="text-xl font-bold text-accent-500">{newThisMonth}</p>
        </div>
        <div className="bg-background-50 border border-background-200 rounded-lg p-4">
          <p className="text-xs text-foreground-500 mb-1">Avg. Lifetime Value</p>
          <p className="text-xl font-bold text-foreground-800">₹{avgLifetimeValue.toLocaleString('en-IN')}</p>
        </div>
      </div>

      {/* Search + Segment filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1 max-w-sm">
          <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-sm text-foreground-400"></i>
          <input
            type="text"
            placeholder="Search name, phone, email..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9 pr-4 py-2 text-sm rounded-lg border border-background-200 bg-background-50 text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 w-full"
          />
        </div>
        <div className="flex items-center gap-1.5 flex-wrap">
          {segments.map((seg) => (
            <button
              key={seg}
              onClick={() => setSegmentFilter(seg)}
              className={`px-3 py-1.5 text-xs font-medium rounded-full border transition-colors cursor-pointer whitespace-nowrap ${
                segmentFilter === seg
                  ? 'bg-primary-500 text-background-50 border-primary-500'
                  : 'bg-background-50 text-foreground-600 border-background-200 hover:border-background-300'
              }`}
            >
              {seg}
              {seg === 'VIP' && ' ⭐'}
              {seg === 'Lapsed' && ' ⚠'}
            </button>
          ))}
        </div>
      </div>

      {/* Customer Table */}
      <div className="bg-background-50 border border-background-200 rounded-lg overflow-hidden">
        {loadError ? (
          <div className="py-16 text-center">
            <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-red-100 flex items-center justify-center">
              <i className="ri-error-warning-line text-xl text-red-500"></i>
            </div>
            <p className="text-sm text-foreground-500 mb-3">{loadError}</p>
            <button
              onClick={load}
              className="px-4 py-2 text-sm font-medium rounded-lg bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors cursor-pointer"
            >
              Retry
            </button>
          </div>
        ) : loading ? (
          <div className="py-16 text-center">
            <i className="ri-loader-4-line text-2xl text-primary-500 animate-spin"></i>
            <p className="text-sm text-foreground-500 mt-2">Loading customers...</p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-background-100 border-b border-background-200">
                  <tr>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-foreground-500 uppercase tracking-wider whitespace-nowrap">Customer</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-foreground-500 uppercase tracking-wider whitespace-nowrap">Phone</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-foreground-500 uppercase tracking-wider whitespace-nowrap">Segment</th>
                    <th
                      className="text-right px-4 py-3 text-xs font-semibold text-foreground-500 uppercase tracking-wider cursor-pointer whitespace-nowrap hover:text-foreground-700"
                      onClick={() => toggleSort('totalVisits')}
                    >
                      Visits {sortKey === 'totalVisits' ? (sortDir === 'asc' ? '↑' : '↓') : ''}
                    </th>
                    <th
                      className="text-right px-4 py-3 text-xs font-semibold text-foreground-500 uppercase tracking-wider cursor-pointer whitespace-nowrap hover:text-foreground-700"
                      onClick={() => toggleSort('totalSpend')}
                    >
                      Total Spend {sortKey === 'totalSpend' ? (sortDir === 'asc' ? '↑' : '↓') : ''}
                    </th>
                    <th className="text-right px-4 py-3 text-xs font-semibold text-foreground-500 uppercase tracking-wider whitespace-nowrap">Avg. Order</th>
                    <th
                      className="text-left px-4 py-3 text-xs font-semibold text-foreground-500 uppercase tracking-wider cursor-pointer whitespace-nowrap hover:text-foreground-700"
                      onClick={() => toggleSort('lastVisit')}
                    >
                      Last Visit {sortKey === 'lastVisit' ? (sortDir === 'asc' ? '↑' : '↓') : ''}
                    </th>
                    <th className="text-right px-4 py-3 text-xs font-semibold text-foreground-500 uppercase tracking-wider whitespace-nowrap">Loyalty Pts</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-foreground-500 uppercase tracking-wider whitespace-nowrap">Tags</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-background-200">
                  {filteredCustomers.map((customer) => (
                    <tr
                      key={customer.id}
                      onClick={() => setSelectedCustomer(customer)}
                      className="hover:bg-background-100/50 transition-colors cursor-pointer"
                    >
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center shrink-0">
                            <span className="text-xs font-semibold text-primary-700">
                              {customer.name.split(' ').map((n) => n[0]).join('')}
                            </span>
                          </div>
                          <div>
                            <p className="font-medium text-foreground-800">{customer.name}</p>
                            <p className="text-xs text-foreground-400">{customer.email ?? '—'}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-sm text-foreground-600 whitespace-nowrap">{customer.phone ?? '—'}</span>
                      </td>
                      <td className="px-4 py-3">
                        <SegmentBadge segment={customer.segment} />
                      </td>
                      <td className="px-4 py-3 text-right">
                        <span className="font-medium text-foreground-800">{customer.totalVisits}</span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <span className="font-medium text-foreground-800 whitespace-nowrap">₹{customer.totalSpend.toLocaleString('en-IN')}</span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <span className="text-foreground-600 whitespace-nowrap">₹{customer.avgOrderValue.toLocaleString('en-IN')}</span>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`text-sm ${isLapsed(customer.lastVisit) ? 'text-red-500' : 'text-foreground-500'}`}>
                          {customer.lastVisit ? formatDate(customer.lastVisit) : 'Never'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <span className="font-medium text-accent-500 whitespace-nowrap">{customer.loyaltyPoints.toLocaleString('en-IN')} pts</span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1 flex-wrap max-w-[180px]">
                          {customer.tags.slice(0, 2).map((tag) => (
                            <span key={tag} className="text-[10px] px-1.5 py-0.5 rounded bg-secondary-100 text-secondary-700 whitespace-nowrap">
                              {tag}
                            </span>
                          ))}
                          {customer.tags.length > 2 && (
                            <span className="text-[10px] text-foreground-400">+{customer.tags.length - 2}</span>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {filteredCustomers.length === 0 && (
              <div className="py-16 text-center">
                <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-background-100 flex items-center justify-center">
                  <i className="ri-user-search-line text-xl text-foreground-300"></i>
                </div>
                <p className="text-sm text-foreground-500">
                  {custs.length === 0 ? 'No customers yet — add your first customer to get started' : 'No customers match your filters'}
                </p>
              </div>
            )}
          </>
        )}
      </div>

      {/* Customer Detail Drawer */}
      {selectedCustomer && (
        <div className="fixed inset-0 z-50 flex justify-end">
          <div className="absolute inset-0 bg-black/30" onClick={() => setSelectedCustomer(null)}></div>
          <div className="relative w-full max-w-md bg-background-50 shadow-lg h-full overflow-y-auto animate-[slideInRight_0.2s_ease-out]">
            <div className="sticky top-0 bg-background-50 border-b border-background-200 px-5 py-4 flex items-center justify-between z-10">
              <h3 className="font-semibold text-foreground-800">Customer Details</h3>
              <button
                onClick={() => setSelectedCustomer(null)}
                className="w-8 h-8 rounded-full flex items-center justify-center hover:bg-background-100 transition-colors cursor-pointer"
              >
                <i className="ri-close-line text-foreground-500"></i>
              </button>
            </div>
            <div className="p-5 space-y-5">
              {/* Profile */}
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 rounded-full bg-primary-100 flex items-center justify-center shrink-0">
                  <span className="text-lg font-bold text-primary-700">
                    {selectedCustomer.name.split(' ').map((n) => n[0]).join('')}
                  </span>
                </div>
                <div>
                  <h4 className="text-base font-semibold text-foreground-800">{selectedCustomer.name}</h4>
                  <SegmentBadge segment={selectedCustomer.segment} />
                </div>
              </div>

              {/* Stats */}
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-background-100 rounded-lg p-3">
                  <p className="text-xs text-foreground-500">Total Visits</p>
                  <p className="text-lg font-bold text-foreground-800">{selectedCustomer.totalVisits}</p>
                </div>
                <div className="bg-background-100 rounded-lg p-3">
                  <p className="text-xs text-foreground-500">Total Spend</p>
                  <p className="text-lg font-bold text-foreground-800">₹{selectedCustomer.totalSpend.toLocaleString('en-IN')}</p>
                </div>
                <div className="bg-background-100 rounded-lg p-3">
                  <p className="text-xs text-foreground-500">Avg Order Value</p>
                  <p className="text-lg font-bold text-foreground-800">₹{selectedCustomer.avgOrderValue.toLocaleString('en-IN')}</p>
                </div>
                <div className="bg-background-100 rounded-lg p-3">
                  <p className="text-xs text-foreground-500">Loyalty Points</p>
                  <p className="text-lg font-bold text-accent-500">{selectedCustomer.loyaltyPoints.toLocaleString('en-IN')}</p>
                  {loyaltyCfg.enabled && (
                    <span className="inline-block mt-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-accent-100 text-accent-700">
                      {tierForPoints(selectedCustomer.loyaltyPoints, loyaltyCfg.tiers)} tier
                    </span>
                  )}
                </div>
                <div className="bg-background-100 rounded-lg p-3">
                  <p className="text-xs text-foreground-500">Wallet Balance</p>
                  <p className="text-lg font-bold text-primary-600">₹{(selectedCustomer.walletBalance ?? 0).toLocaleString('en-IN')}</p>
                </div>
              </div>

              {/* Contact */}
              <div className="space-y-3">
                <div className="flex items-center gap-2 text-sm">
                  <i className="ri-phone-line text-foreground-400 w-5 h-5 flex items-center justify-center"></i>
                  <span className="text-foreground-700">{selectedCustomer.phone ?? '—'}</span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <i className="ri-mail-line text-foreground-400 w-5 h-5 flex items-center justify-center"></i>
                  <span className="text-foreground-700">{selectedCustomer.email ?? '—'}</span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <i className="ri-calendar-line text-foreground-400 w-5 h-5 flex items-center justify-center"></i>
                  <span className="text-foreground-700">Joined {selectedCustomer.joinedDate ? formatDate(selectedCustomer.joinedDate) : '—'}</span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <i className="ri-user-smile-line text-foreground-400 w-5 h-5 flex items-center justify-center"></i>
                  <span className="text-foreground-700">{selectedCustomer.dietaryPrefs.length > 0 ? selectedCustomer.dietaryPrefs.join(', ') : 'No preferences'}</span>
                </div>
              </div>

              {/* Tags */}
              <div>
                <p className="text-xs font-semibold text-foreground-500 uppercase mb-2">Tags</p>
                <div className="flex items-center gap-1.5 flex-wrap">
                  {selectedCustomer.tags.length > 0 ? selectedCustomer.tags.map((tag) => (
                    <span key={tag} className="text-xs px-2 py-1 rounded-full bg-secondary-100 text-secondary-700">
                      {tag}
                    </span>
                  )) : <span className="text-xs text-foreground-400">No tags</span>}
                </div>
              </div>

              {/* Visit History */}
              <div>
                <p className="text-xs font-semibold text-foreground-500 uppercase mb-2">Visit History</p>
                <p className="text-xs text-foreground-400">Visit history will appear as orders are linked.</p>
              </div>

              {/* Action buttons */}
              <div className="space-y-2 pt-2 border-t border-background-200">
                <button
                  onClick={() => openEditCustModal(selectedCustomer)}
                  className="w-full px-4 py-2.5 text-sm font-medium rounded-lg bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors cursor-pointer"
                >
                  <i className="ri-edit-line mr-1.5"></i> Edit Customer
                </button>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    onClick={() => handleRedeemPoints(selectedCustomer)}
                    className="px-3 py-2.5 text-sm font-medium rounded-lg border border-background-200 text-foreground-700 hover:bg-background-100 transition-colors cursor-pointer"
                  >
                    <i className="ri-coupon-line mr-1.5"></i> Redeem
                  </button>
                  <button
                    onClick={() => handleAdjustPoints(selectedCustomer)}
                    className="px-3 py-2.5 text-sm font-medium rounded-lg border border-background-200 text-foreground-700 hover:bg-background-100 transition-colors cursor-pointer"
                  >
                    <i className="ri-gift-line mr-1.5"></i> Adjust
                  </button>
                  <button
                    onClick={() => handleWallet(selectedCustomer, 1)}
                    className="px-3 py-2.5 text-sm font-medium rounded-lg border border-background-200 text-foreground-700 hover:bg-background-100 transition-colors cursor-pointer"
                  >
                    <i className="ri-wallet-3-line mr-1.5"></i> Add Credit
                  </button>
                  <button
                    onClick={() => handleWallet(selectedCustomer, -1)}
                    className="px-3 py-2.5 text-sm font-medium rounded-lg border border-background-200 text-foreground-700 hover:bg-background-100 transition-colors cursor-pointer"
                  >
                    <i className="ri-wallet-3-line mr-1.5"></i> Deduct
                  </button>
                </div>
                <button
                  onClick={() => setDeleteTarget(selectedCustomer)}
                  className="w-full px-4 py-2.5 text-sm font-medium rounded-lg border border-red-200 text-red-600 hover:bg-red-50 transition-colors cursor-pointer"
                >
                  <i className="ri-delete-bin-line mr-1.5"></i> Delete Customer
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add/Edit Customer Modal */}
      {custModalOpen && (
        <div className="fixed inset-0 z-50 flex items-start justify-center pt-[10vh]">
          <div className="absolute inset-0 bg-black/30" onClick={() => { setCustModalOpen(false); setEditingCustomer(null); }}></div>
          <div className="relative w-full max-w-md bg-background-50 rounded-xl shadow-lg mx-4 animate-[slideInUp_0.2s_ease-out]">
            <div className="sticky top-0 bg-background-50 border-b border-background-200 px-5 py-4 flex items-center justify-between z-10">
              <h3 className="font-semibold text-foreground-800">{editingCustomer ? 'Edit Customer' : 'Add Customer'}</h3>
              <button onClick={() => { setCustModalOpen(false); setEditingCustomer(null); }} className="w-8 h-8 rounded-full flex items-center justify-center hover:bg-background-100 transition-colors cursor-pointer">
                <i className="ri-close-line text-foreground-500"></i>
              </button>
            </div>
            <div className="p-5 space-y-4">
              <div>
                <label className="block text-xs font-medium text-foreground-500 mb-1">Name *</label>
                <input type="text" value={custForm.name} onChange={(e) => setCustForm({ ...custForm, name: e.target.value })} placeholder="Customer name" className="w-full px-3 py-2 text-sm rounded-lg border border-background-200 bg-background-50 text-foreground-800 focus:outline-none focus:border-primary-300" />
              </div>
              <div>
                <label className="block text-xs font-medium text-foreground-500 mb-1">Phone *</label>
                <input type="tel" value={custForm.phone} onChange={(e) => setCustForm({ ...custForm, phone: e.target.value })} placeholder="+91 98765 43210" className="w-full px-3 py-2 text-sm rounded-lg border border-background-200 bg-background-50 text-foreground-800 focus:outline-none focus:border-primary-300" />
              </div>
              <div>
                <label className="block text-xs font-medium text-foreground-500 mb-1">Email</label>
                <input type="email" value={custForm.email} onChange={(e) => setCustForm({ ...custForm, email: e.target.value })} placeholder="customer@email.com" className="w-full px-3 py-2 text-sm rounded-lg border border-background-200 bg-background-50 text-foreground-800 focus:outline-none focus:border-primary-300" />
              </div>
              <div>
                <label className="block text-xs font-medium text-foreground-500 mb-1">Segment</label>
                <select value={custForm.segment} onChange={(e) => setCustForm({ ...custForm, segment: e.target.value as Customer['segment'] })} className="w-full px-3 py-2 text-sm rounded-lg border border-background-200 bg-background-50 text-foreground-800 focus:outline-none focus:border-primary-300 cursor-pointer">
                  {['VIP','Regular','New','Lapsed'].map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-foreground-500 mb-1">Dietary Preferences (comma-separated)</label>
                <input type="text" value={custForm.dietaryPrefs} onChange={(e) => setCustForm({ ...custForm, dietaryPrefs: e.target.value })} placeholder="e.g. Jain, No Onion Garlic" className="w-full px-3 py-2 text-sm rounded-lg border border-background-200 bg-background-50 text-foreground-800 focus:outline-none focus:border-primary-300" />
              </div>
              <div>
                <label className="block text-xs font-medium text-foreground-500 mb-1">Tags (comma-separated)</label>
                <input type="text" value={custForm.tags} onChange={(e) => setCustForm({ ...custForm, tags: e.target.value })} placeholder="e.g. Regular, Lunch" className="w-full px-3 py-2 text-sm rounded-lg border border-background-200 bg-background-50 text-foreground-800 focus:outline-none focus:border-primary-300" />
              </div>
              <div className="flex items-center gap-3 pt-3 border-t border-background-200">
                <button onClick={handleCustSave} disabled={saving} className="px-5 py-2 text-sm font-medium rounded-lg bg-accent-500 text-background-50 hover:bg-accent-600 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60">
                  {saving ? 'Saving...' : editingCustomer ? 'Save Changes' : 'Add Customer'}
                </button>
                <button onClick={() => { setCustModalOpen(false); setEditingCustomer(null); }} className="px-5 py-2 text-sm font-medium rounded-lg border border-background-200 text-foreground-600 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap">
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirm */}
      {deleteTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/30" onClick={() => setDeleteTarget(null)}></div>
          <div className="relative w-full max-w-sm bg-background-50 rounded-xl shadow-lg mx-4 p-5 animate-[slideInUp_0.15s_ease-out]">
            <div className="w-10 h-10 mx-auto mb-3 rounded-full bg-red-100 flex items-center justify-center">
              <i className="ri-delete-bin-line text-red-500 text-lg"></i>
            </div>
            <h4 className="text-center font-semibold text-foreground-800 mb-1">Delete Customer?</h4>
            <p className="text-center text-sm text-foreground-500 mb-4">"{deleteTarget.name}" will be permanently removed.</p>
            <div className="flex items-center gap-3">
              <button onClick={() => setDeleteTarget(null)} className="flex-1 px-4 py-2 text-sm font-medium rounded-lg border border-background-200 text-foreground-600 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap">Cancel</button>
              <button onClick={() => handleDelete(deleteTarget)} className="flex-1 px-4 py-2 text-sm font-medium rounded-lg bg-red-500 text-background-50 hover:bg-red-600 transition-colors cursor-pointer whitespace-nowrap">Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function SegmentBadge({ segment }: { segment: string }) {
  const styles: Record<string, string> = {
    VIP: 'bg-accent-100 text-accent-800',
    Regular: 'bg-primary-100 text-primary-700',
    New: 'bg-green-100 text-green-700',
    Lapsed: 'bg-red-100 text-red-700',
  };
  return (
    <span className={`inline-flex text-xs px-2 py-0.5 rounded-full font-medium whitespace-nowrap ${styles[segment] || 'bg-background-100 text-foreground-600'}`}>
      {segment}
    </span>
  );
}

function formatDate(dateStr: string): string {
  const d = new Date(dateStr);
  const now = new Date();
  const diffDays = Math.floor((now.getTime() - d.getTime()) / (1000 * 60 * 60 * 24));
  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return `${diffDays}d ago`;
  return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: '2-digit' });
}

function isLapsed(dateStr: string | null): boolean {
  if (!dateStr) return false;
  const d = new Date(dateStr);
  const now = new Date();
  const diffDays = Math.floor((now.getTime() - d.getTime()) / (1000 * 60 * 60 * 24));
  return diffDays > 14;
}
